import debounce from 'lodash.debounce';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import { DATADOG_ACTIONS } from '@cartwhl/tracking/datadogConstants';
import FrontDoor from '@cartwhl/ui/feature/FrontDoor';
import PromptInput from '@cartwhl/ui/input/PromptInput';

import { BrowseSearchPreview } from 'components/BrowseMotions';
import { browsePoseStore, noticesStore, posingStore } from 'stores';
import { PoseSuggestion } from 'stores/browsePoseStore';
import { handleKeyDown } from 'utils/hotkeys';

import { PLACEHOLDERS_POSE } from '../constants';
import { usePlaceholders } from '../hooks';

export default function TextToPose() {
  const [submitting, setSubmitting] = useState(false);
  const { suggestions } = useSnapshot(browsePoseStore.state);
  const [highlightedSuggestionID, setHighlightedSuggestionID] =
    useState<string>(suggestions.data[0]?.jobID || '');
  const [isSuggestionsActive, setIsSuggestionsActive] = useState(false);

  useEffect(() => {
    setHighlightedSuggestionID(suggestions.data[0]?.jobID || '');
    setIsSuggestionsActive(false);
  }, [suggestions.data]);

  const [text, setText] = useState('');
  const abortControllerRef = useRef<AbortController | null>(null);

  const placeholder = usePlaceholders(PLACEHOLDERS_POSE);

  const showSuggestions = suggestions.data.length > 0;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setSubmitting(true);
    const jobID = highlightedSuggestionID || suggestions.data[0]?.jobID;
    if (jobID) {
      await posingStore.loadPoseFromJobID(jobID);
    } else {
      noticesStore.info(
        'Your input could not be converted to a pose. Please try a different prompt.',
      );
    }
    setSubmitting(false);
  };

  const debouncedSearch = useRef(
    debounce((searchTerm: string) => {
      cleanupSearch();
      abortControllerRef.current = new AbortController();
      browsePoseStore.initSearchSuggestions({ searchTerm });
    }, 300),
  ).current;

  const cleanupSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };
  useEffect(() => {
    debouncedSearch(text);
    // intentionally empty dependency array to run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      cleanupSearch();
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <>
      <FrontDoor.FadeContent
        show={showSuggestions}
        className="dark:bg-gray-90 border-primary max-w-[672px] flex-1 rounded-t-2xl border-b bg-white"
      >
        <BrowseSearchPreview
          suggestions={suggestions}
          highlightedSuggestionID={highlightedSuggestionID}
        />
      </FrontDoor.FadeContent>
      <PromptInput
        active={!isSuggestionsActive}
        buttonVariant="pose"
        buttonText="Set Pose"
        placeholder={placeholder}
        prompt={text}
        hideCursor={isSuggestionsActive}
        onChange={(e) => {
          setText(e.target.value);
          debouncedSearch(e.target.value);
        }}
        onSubmit={handleSubmit}
        onKeyDown={(e) =>
          handleKeyDown(
            e,
            showSuggestions,
            suggestions as {
              searchTerm: string;
              data: PoseSuggestion[];
              loading: boolean;
              total: number;
            },
            highlightedSuggestionID,
            setHighlightedSuggestionID,
            isSuggestionsActive,
            setIsSuggestionsActive,
          )
        }
        loading={submitting}
        className={`flex-none ${showSuggestions ? 'rounded-t-none' : ''}`}
        datadogActionName={DATADOG_ACTIONS.GENERATE_POSE}
      />
    </>
  );
}
